import {Component, inject, input, model, resource} from '@angular/core';
import {take} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {DxDataGridModule, DxLoadPanelModule, DxPopupModule} from 'devextreme-angular';
import {confirm} from 'devextreme/ui/dialog';
import {SharedModule} from '../../../../shared/shared.module';
import {FactureExterneDTO} from '../../../../core/dtos/facturation/fournisseurs/factures/facture-externe-dto';
import {FactureExterneRapprochementDTO} from '../../../../core/dtos/facturation/fournisseurs/factures/facture-externe-rapprochement-dto';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {RapprochementFactureExterneStatutEnum, RAPPROCHEMENT_FACTURE_EXTERNE_STATUT_LABELS} from '../../../../core/enums/facturation/fournisseur/rapprochement-facture-externe-statut-enum';
import {FactureExterneStatutEnum, FACTURE_EXTERNE_STATUT_LABELS} from '../../../../core/enums/facturation/fournisseur/facture-externe-statut-enum';
import {FactureExterneService} from '../../../../core/services/entities/facture-externe.service';
import {Router} from '@angular/router';

@Component({
  selector: 'yo-facture-externe-dialog',
  templateUrl: './facture-externe-dialog.component.html',
  styleUrls: ['./facture-externe-dialog.component.scss'],
  imports: [CommonModule, DxPopupModule, DxDataGridModule, DxLoadPanelModule, SharedModule]
})
export class FactureExterneDialogComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;
  protected readonly RapprochementStatutEnum = RapprochementFactureExterneStatutEnum;
  protected readonly FactureExterneStatutEnum = FactureExterneStatutEnum;
  protected readonly factureStatutLabels = FACTURE_EXTERNE_STATUT_LABELS;
  protected readonly rapprochementStatutLabels = RAPPROCHEMENT_FACTURE_EXTERNE_STATUT_LABELS;

  private readonly factureExterneSvc = inject(FactureExterneService);
  private readonly router = inject(Router);

  readonly displayDialog = model<boolean>(false);
  readonly factureId = input<number | null>(null);
  readonly dialogTitle = input<string>('Facture externe');

  readonly factureResource = resource<FactureExterneDTO | null, number | null>({
    params: () => this.factureId(),
    loader: async ({params: id}) => {
      if (!id) return null;
      return this.factureExterneSvc.findById(id).pipe(take(1)).toPromise();
    }
  });

  closeDialog = (): void => this.displayDialog.set(false);

  getRapprochementTvaTaux = (facture: FactureExterneDTO, slot: 1 | 2 | 3 | 4 | 5): number | null => {
    const key = `tva${slot}Taux` as keyof FactureExterneRapprochementDTO;
    return facture.rapprochementList?.find(r => r[key] != null)?.[key] as number ?? null;
  };

  openFactureInterne = (factureCommandeFournisseurId: number): void => {
    this.closeDialog();
    this.router.navigate(['/factures/fournisseurs', factureCommandeFournisseurId]);
  };

  validerEcart = async (): Promise<void> => {
    const facture = this.factureResource.value();
    if (!facture) return;
    const isValidated: boolean = await confirm(`Voulez-vous valider l'écart de la facture externe n° ${facture.numero} ?`, "Validation de l'écart");
    if (!isValidated) return;
    this.closeDialog();
    this.factureExterneSvc.validerEcart(facture.id).subscribe(() => {
      this.factureExterneSvc.factureExterneResource.update(list =>
        (list ?? []).map(f => f.id === facture.id
          ? {...f, rapprochementStatut: RapprochementFactureExterneStatutEnum.ECART_VALIDE}
          : f
        )
      );
      this.factureResource.update(f => f ? {...f, rapprochementStatut: RapprochementFactureExterneStatutEnum.ECART_VALIDE} : f);
    });
  };

  abandonner = async (): Promise<void> => {
    const facture = this.factureResource.value();
    if (!facture) return;
    const isConfirmed: boolean = await confirm(`Voulez-vous abandonner la facture externe n° ${facture.numero} ?`, 'Abandon de la facture');
    if (!isConfirmed) return;
    this.closeDialog();
    this.factureExterneSvc.abandonner(facture.id).subscribe(() => {
      this.factureExterneSvc.factureExterneResource.update(list =>
        (list ?? []).map(f => f.id === facture.id
          ? {...f, statut: FactureExterneStatutEnum.ABANDONNEE}
          : f
        )
      );
    });
  };

  refuser = async (): Promise<void> => {
    const facture = this.factureResource.value();
    if (!facture) return;
    const isConfirmed: boolean = await confirm(`Voulez-vous refuser la facture externe n° ${facture.numero} ?`, 'Refus de la facture');
    if (!isConfirmed) return;
    this.closeDialog();
    this.factureExterneSvc.refuser(facture.id).subscribe(() => {
      this.factureExterneSvc.factureExterneResource.update(list =>
        (list ?? []).map(f => f.id === facture.id
          ? {...f, statut: FactureExterneStatutEnum.REFUSEE}
          : f
        )
      );
    });
  };

  protected validerRapprochement = async (): Promise<void> => {
    const facture = this.factureResource.value();
    if (!facture) return;
    const isValidated: boolean = await confirm(`Voulez-vous valider la facture externe n° ${facture.numero} ?`, "Validation de la facture");
    if (!isValidated) return;
    this.closeDialog();
    this.factureExterneSvc.valider(facture.id).subscribe(() => {
      this.factureExterneSvc.factureExterneResource.update(list =>
        (list ?? []).map(f => f.id === facture.id
          ? {...f, statut: FactureExterneStatutEnum.VALIDEE_MANUELLEMENT}
          : f
        )
      );
      this.factureResource.update(f => f ? {...f, statut: FactureExterneStatutEnum.VALIDEE_MANUELLEMENT} : f);
    });
  };
}
